// Notifications derived from real account data. The API has no notification
// feed, so the bell shows what the customer can act on: open/overdue invoices
// and hostings that are suspended or being set up. Read state is per browser.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from './api.js';
import { formatDate, formatMoney } from './format.js';
import { isOpenInvoice, isOverdueInvoice } from './status.js';
import { t } from './i18n.jsx';

const READ_KEY = 'notif.read';
const REFRESH_MS = 5 * 60 * 1000;

function loadRead() {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function saveRead(set) {
  try {
    // Keep the list bounded; ids of resolved items are no longer needed.
    localStorage.setItem(READ_KEY, JSON.stringify(Array.from(set).slice(-200)));
  } catch {
    /* storage full or disabled — read state just won't persist */
  }
}

function build(invoices, hostings, lang) {
  const out = [];
  for (const inv of invoices || []) {
    if (!isOpenInvoice(inv)) continue;
    const overdue = isOverdueInvoice(inv);
    out.push({
      // The id includes the status so a newly overdue invoice shows up as unread again.
      id: `inv-${inv.id}-${overdue ? 'overdue' : inv.status}`,
      tone: overdue || inv.status === 'failed' ? 'warn' : 'acc',
      icon: overdue || inv.status === 'failed' ? 'warn' : 'info',
      title: overdue
        ? t(lang, 'notif.invoice.overdue', { number: inv.invoice_number })
        : inv.status === 'failed'
          ? t(lang, 'notif.invoice.failed', { number: inv.invoice_number })
          : t(lang, 'notif.invoice.open', { number: inv.invoice_number }),
      body: t(lang, 'notif.invoice.body', { amount: formatMoney(inv.total_amount, lang), date: formatDate(inv.due_date, lang) }),
      target: `/facturatie/${inv.id}`,
    });
  }
  for (const h of hostings || []) {
    if (h.status === 'suspended') {
      out.push({
        id: `host-${h.id}-suspended`,
        tone: 'warn',
        icon: 'warn',
        title: t(lang, 'notif.hosting.suspended', { domain: h.domain }),
        body: t(lang, 'notif.hosting.suspended.body'),
        target: `/hosting/${h.id}`,
      });
    } else if (h.status === 'pending') {
      out.push({
        id: `host-${h.id}-pending`,
        tone: 'acc',
        icon: 'info',
        title: t(lang, 'notif.hosting.pending', { domain: h.domain }),
        body: t(lang, 'notif.hosting.pending.body'),
        target: `/hosting/${h.id}`,
      });
    } else if (h.status === 'active' && h.health_status === 'down') {
      out.push({
        id: `host-${h.id}-down`,
        tone: 'warn',
        icon: 'warn',
        title: t(lang, 'notif.hosting.down', { domain: h.domain }),
        body: t(lang, 'notif.hosting.down.body'),
        target: `/hosting/${h.id}`,
      });
    }
  }
  return out;
}

export function useNotifications(lang) {
  const [invoices, setInvoices] = useState(null);
  const [hostings, setHostings] = useState(null);
  const [error, setError] = useState(false);
  const [readIds, setReadIds] = useState(loadRead);

  const load = useCallback(async () => {
    const [inv, host] = await Promise.allSettled([get('/invoices'), get('/hostings')]);
    if (inv.status === 'fulfilled') setInvoices(inv.value);
    if (host.status === 'fulfilled') setHostings(host.value);
    setError(inv.status === 'rejected' && host.status === 'rejected');
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const items = useMemo(
    () => build(invoices, hostings, lang).map((n) => ({ ...n, read: readIds.has(n.id) })),
    [invoices, hostings, lang, readIds],
  );

  const markRead = useCallback((ids) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      saveRead(next);
      return next;
    });
  }, []);

  return {
    items,
    loading: invoices === null && hostings === null && !error,
    error,
    reload: load,
    markRead,
  };
}
